import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import colours from 'theme/atoms/colours';
import typography from 'theme/atoms/typography';
import { SecondaryLight } from 'theme/components/Buttons.style';
import * as Text from 'theme/components/Text.style';

export const SectionHeader = styled(Text.LabelSemibold)`
  color: ${colours.neutral[600]};
  margin: 0;
  padding: 15px;
  text-transform: uppercase;
`;

type ItemButtonFlags = {
  $primary?: boolean;
  $selected?: boolean;
  $destructive?: boolean;
};

function itemBg(p: ItemButtonFlags, fallback: string): string {
  if (p.$primary) return colours.primary[600];
  if (p.$selected) return colours.neutral[900];
  return fallback;
}

function itemHoverBg(p: ItemButtonFlags): string {
  if (p.$primary) return colours.primary[700];
  if (p.$selected) return colours.neutral[800];
  return colours.neutral[100];
}

function itemActiveBg(p: ItemButtonFlags): string {
  if (p.$primary) return colours.primary[800];
  if (p.$selected) return colours.neutral[800];
  return colours.neutral[200];
}

function itemFg(p: ItemButtonFlags): string {
  if (p.$primary || p.$selected) return 'white';
  if (p.$destructive) return colours.primary[500];
  return 'inherit';
}

export const Panel = styled(motion.div)<{
  $width?: string;
  $hasSearch?: boolean;
  $openUp?: boolean;
}>`
  position: absolute;
  right: 0;
  ${({ $openUp }) =>
    $openUp
      ? css`
          bottom: calc(100% + 10px);
          top: auto;
          transform-origin: bottom right;
        `
      : css`
          top: calc(100% + 10px);
          bottom: auto;
          transform-origin: top right;
        `}
  background-color: white;
  border-radius: 35px;
  border: 2px solid ${colours.neutral[200]};
  box-shadow: 0px 50px 70px -35px rgba(0, 0, 0, 0.15);
  min-width: 260px;
  padding: ${(p) => (p.$hasSearch ? '0' : '10px')};
  width: ${(p) => p.$width || 'fit-content'};
  z-index: 1300;
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$hasSearch ? '0' : '10px')};
  ${(p) =>
    p.$hasSearch
      ? 'overflow: hidden;'
      : `
        max-height: 320px;
        overflow-y: auto;
        overflow-x: hidden;
        scrollbar-width: none;
        -ms-overflow-style: none;
        &::-webkit-scrollbar {
          display: none;
        }
      `}
`;

export const ItemButton = styled(motion.button)<{
  disabled?: boolean;
  $primary?: boolean;
  $selected?: boolean;
  $destructive?: boolean;
  $hasSubContent?: boolean;
}>`
  background-color: ${(p) => itemBg(p, 'transparent')};
  border-radius: ${(p) => (p.$hasSubContent ? '15px' : '30px')};
  border: none;
  color: ${(p) => (itemFg(p) === 'inherit' ? '#001d25' : itemFg(p))};
  display: flex;
  gap: 10px;
  padding: ${(p) => (p.$hasSubContent ? '15px' : '15px 15px 15px 20px')};
  align-items: center;
  min-height: ${(p) => (p.$hasSubContent ? 'auto' : '50px')};
  transition: background-color 0.2s ease;
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.disabled ? 0.5 : 1)};
  width: 100%;
  text-align: left;
  justify-content: space-between;
  ${typography.p2}

  &:hover:not(:disabled) {
    background-color: ${(p) => itemHoverBg(p)};
  }

  &:active:not(:disabled) {
    background-color: ${(p) => itemActiveBg(p)};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid ${colours.primary[500]};
    outline-offset: 2px;
  }

  &:disabled {
    cursor: not-allowed;
  }

  p {
    color: ${(p) => itemFg(p)};
    margin: 0;
  }

  svg {
    color: ${(p) => itemFg(p)};
  }
`;

export const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  color: inherit;

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const ItemLeadingIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const ItemSubContent = styled(Text.Label).attrs({ as: 'span' })`
  color: ${colours.neutral[600]};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const SearchHeader = styled.div`
  position: sticky;
  top: 0;
  background: white;
  padding: 10px 10px 0;
  z-index: 1;
`;

export const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const SearchInput = styled.input`
  border: 2px solid ${colours.neutral[200]};
  border-radius: 30px;
  padding: 15px 20px 15px 45px;
  width: 100%;
  ${typography.p2}
  color: ${colours.neutral[900]};
  background: white;
  outline: none;
  transition: border-color 0.15s ease;

  &::placeholder {
    color: ${colours.neutral[600]};
  }

  &:focus {
    border-color: ${colours.neutral[300]};
  }
`;

export const SearchInputIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  pointer-events: none;
  color: ${colours.neutral[600]};
`;

export const ItemsList = styled.div`
  max-height: 335px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  gap: 10px;
  display: flex;
  flex-direction: column;
`;

export const NoResults = styled.p`
  ${typography.p2}
  color: ${colours.neutral[500]};
  text-align: center;
  margin: 0;
  padding: 20px 10px;
`;

export const FooterWrapper = styled.div`
  padding: 0 10px 10px;
`;

export const FooterButton = styled(SecondaryLight).attrs({
  $size: 'medium',
  stretch: true,
})`
  gap: 10px;
`;

export const Root = styled('div')`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 44px;
  min-height: 44px;
`;
